/**
 * VisitorNavBar - Top navigation bar for Visitor World pages
 * Now driven by the unified AuthContext (Supabase session).
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, CreditCard, ArrowLeft, Bell } from 'lucide-react';
import ProfileSetupModal from '../Auth/ProfileSetupModal';
import mudoLogo from '../../assets/mudo-logo-bg.png';
import InboxModal from '../Inbox/InboxModal';
import { useNotificationsForWorld } from '../../contexts/NotificationsContext';
import { useAuth } from '../../contexts/AuthContext';
import AvatarPickerModal from '../Avatars/AvatarPickerModal';
import { persistAvatarForCurrentUser } from '../../lib/avatarPersistence';

export default function VisitorNavBar({ showBackButton = false, backTo = '/discover' }) {
  const navigate = useNavigate();
  const { user, visitorProfile, profile, signOut } = useAuth() || {};
  const [menuOpen, setMenuOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const menuRef = useRef(null);
  const { unreadCount } = useNotificationsForWorld("visitor");

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  const handleLogout = async () => {
    try {
      await signOut?.();
      setMenuOpen(false);
      navigate('/discover');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const displayName =
    visitorProfile?.display_name ||
    profile?.display_name ||
    user?.email?.split('@')[0] ||
    'Guest';
  const avatarEmoji =
    visitorProfile?.avatar_emoji || profile?.avatar_emoji || ':)';
  const avatarUrl = visitorProfile?.avatar_url || profile?.avatar_url || null;
  const isLoggedIn = !!user;

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[3000] flex items-center justify-between px-6 py-4">
        {/* Left: Back button or Logo */}
        {showBackButton ? (
          <button
            onClick={() => navigate(backTo)}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/15 transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-semibold">Back</span>
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <img 
              src={mudoLogo} 
              alt="Mudo" 
              className="w-8 h-8"
              loading="eager"
              decoding="async"
              width={32}
              height={32}
            />
            <span className="text-xl font-bold">Mudo</span>
          </div>
        )}

        {/* Right: Profile Menu & Logout */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setInboxOpen(true)}
            className="relative flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition hover:bg-white/15"
            aria-label="Open inbox"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 rounded-full bg-orange-500 px-1.5 text-xs font-semibold text-black">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {isLoggedIn ? (
            <>
              {/* Profile Dropdown */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/15 transition-all duration-300"
                >
                  <span className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center bg-white/10 border border-white/10">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl">{avatarEmoji}</span>
                    )}
                  </span>
                  <span className="font-semibold hidden sm:inline">{displayName}</span>
                  <User className="w-4 h-4" />
                </button>

                {/* Dropdown Menu */}
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-64 rounded-2xl bg-neutral-900/95 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden z-50">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10 border border-white/10 flex items-center justify-center">
                          {avatarUrl ? (
                            <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-3xl">{avatarEmoji}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-white truncate">{displayName}</div>
                          <div className="text-xs text-white/60 truncate">{user?.email || ""}</div>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          navigate("/discover/profile");
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-white hover:bg-white/5 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Profile</span>
                      </button>

                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          navigate("/discover/my-offers");
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-white hover:bg-white/5 transition-colors"
                      >
                        <CreditCard className="w-4 h-4" />
                        <span>My offers</span>
                      </button>

                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/discover/my-bookings');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-white hover:bg-white/5 transition-colors"
                      >
                        <CreditCard className="w-4 h-4" />
                        <span>My Bookings</span>
                      </button>

                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/discover/my-pieces');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-white hover:bg-white/5 transition-colors"
                      >
                        <CreditCard className="w-4 h-4" />
                        <span>My Pieces</span>
                      </button>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-white/10 py-2">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Log Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Standalone Logout Button - Always Visible */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/20 backdrop-blur-md border border-red-500/30 hover:bg-red-600/30 text-red-300 hover:text-red-200 transition-all duration-300"
                title="Log Out"
              >
                <LogOut className="w-4 h-4" />
                <span className="font-semibold hidden sm:inline">Log Out</span>
              </button>
            </>
          ) : (
            /* Login Button - Not Logged In */
            <button
              onClick={() => navigate("/auth")}
              className="flex items-center gap-3 px-5 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/15 transition-all duration-300"
            >
              <User className="w-5 h-5" />
              <span className="font-semibold">Log In</span>
            </button>
          )}
        </div>
      </div>

      {/* Profile Setup Modal */}
      <ProfileSetupModal
        isOpen={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
        }}
        onComplete={() => {
          setProfileModalOpen(false);
        }}
      />

      <AvatarPickerModal
        open={avatarPickerOpen}
        initialUrl={avatarUrl}
        onClose={() => setAvatarPickerOpen(false)}
        onSave={async (nextUrl) => {
          setAvatarPickerOpen(false);
          const res = await persistAvatarForCurrentUser({
            avatarUrl: nextUrl,
            avatarEmoji: nextUrl ? null : avatarEmoji,
          });
          if (!res?.ok) {
            console.warn("[VisitorNavBar] persist avatar failed", res?.error);
          }
          try {
            window.dispatchEvent(new Event("mudo:profile-refresh"));
          } catch {}
        }}
      />
      {inboxOpen && (
        <InboxModal world="visitor" open={inboxOpen} onClose={() => setInboxOpen(false)} />
      )}
    </>
  );
}
